import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  BadgeCheck,
  CalendarDays,
  Copy,
  Download,
  Eye,
  FileText,
  Loader2,
  Mail,
  MapPin,
  PenLine,
  RotateCcw,
  Send,
  ShieldCheck,
  SquarePen,
  Upload,
  UserX,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Timeline } from '@/components/onboarding/timeline'
import { EditStarterDialog } from '@/components/onboarding/edit-starter-dialog'
import { ProgressBar, StarterStatusBadge } from '@/components/onboarding/status'
import { Panel } from '@/components/quotes/quote-panels'
import { TypedSignature } from '@/components/proposal/signature-pad'
import { PageHeader } from '@/components/shell/page'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import { ukDate } from '@/lib/quotes'
import { can } from '@/lib/roles'
import { reportError } from '@/lib/settings'
import {
  ANSWER_LABELS,
  fileUrl,
  formatSize,
  KIND_LABEL,
  starterQuery,
  type StarterDetail,
  type StarterFile,
  type StarterTask,
} from '@/lib/starters'
import { useTitle } from '@/lib/use-title'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/onboarding/$id')({ component: StarterPage })

const KIND_ICON = { sign: PenLine, form: SquarePen, acknowledge: FileText, upload: Upload }

function StarterPage() {
  const { id } = Route.useParams()
  const { me } = Route.useRouteContext()
  const allowed = can.managePeople(me)
  const qc = useQueryClient()
  const q = useQuery({ ...starterQuery(id), enabled: allowed })
  const [editing, setEditing] = useState(false)
  useTitle(q.data ? q.data.name : 'Starter')
  const refresh = (s: StarterDetail) => {
    qc.setQueryData(starterQuery(id).queryKey, s)
    void qc.invalidateQueries({ queryKey: ['starters'] })
  }

  const approve = useMutation({
    mutationFn: () =>
      api.post<StarterDetail>(`/starters/${id}/approve`, { expectedVersion: q.data!.version }),
    onSuccess: (s) => {
      refresh(s)
      toast.success('Starter approved')
    },
    onError: (e) => reportError(e, () => void q.refetch()),
  })
  const withdraw = useMutation({
    mutationFn: () =>
      api.post<StarterDetail>(`/starters/${id}/withdraw`, { expectedVersion: q.data!.version }),
    onSuccess: (s) => {
      refresh(s)
      toast.success('Starter withdrawn. Their link no longer works.')
    },
    onError: (e) => reportError(e, () => void q.refetch()),
  })

  if (!allowed)
    return (
      <p className="rounded-lg border bg-muted p-4 text-[15px]">
        New starters' details are private. Ask an owner or HR admin if you need access.
      </p>
    )
  if (q.isError)
    return (
      <p role="alert" className="text-destructive">
        {q.error.message}
      </p>
    )
  if (q.isPending)
    return (
      <div className="grid gap-4">
        <Skeleton className="h-16 w-1/2" />
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    )
  const s = q.data
  const open = s.status !== 'approved' && s.status !== 'withdrawn'
  const uncheckedUploads = s.tasks.filter(
    (t) => t.kind === 'upload' && t.status === 'done' && !t.reviewedAt,
  )
  const ready =
    s.status === 'submitted' &&
    s.progress.requiredDone === s.progress.required &&
    s.progress.rejected === 0 &&
    uncheckedUploads.length === 0

  return (
    <>
      <Link
        to="/onboarding"
        className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden />
        All starters
      </Link>
      <PageHeader
        title={s.name}
        description={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <StarterStatusBadge status={s.status} />
            {s.jobTitle && <span>{s.jobTitle}</span>}
            {s.startDate && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="size-4" aria-hidden />
                Starts {ukDate(s.startDate)}
              </span>
            )}
            {s.site && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="size-4" aria-hidden />
                {s.site}
              </span>
            )}
          </span>
        }
        actions={
          open ? (
            <Button variant="outline" className="h-10" onClick={() => setEditing(true)}>
              <SquarePen />
              Edit details
            </Button>
          ) : undefined
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="grid min-w-0 content-start gap-4">
          {s.status === 'submitted' && (
            <section
              className={cn(
                'flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center',
                ready ? 'border-success/40 bg-success/5' : 'border-warning/40 bg-warning/5',
              )}
            >
              <ShieldCheck
                className={cn('size-6 shrink-0', ready ? 'text-success' : 'text-warning')}
                aria-hidden
              />
              <div className="flex-1 text-[15px]">
                <p className="font-semibold">
                  {ready ? 'Everything is checked' : 'Ready for you to review'}
                </p>
                <p className="text-muted-foreground">
                  {ready
                    ? 'Approve the pack to finish onboarding. Their link will stop working.'
                    : uncheckedUploads.length
                      ? `Check the uploaded documents first: ${uncheckedUploads.map((t) => t.name).join(', ')}. Mark each as checked once you have seen the originals.`
                      : 'Some items still need attention.'}
                </p>
              </div>
              <Button
                className="h-10"
                disabled={!ready || approve.isPending}
                onClick={() => approve.mutate()}
              >
                {approve.isPending ? <Loader2 className="animate-spin" /> : <BadgeCheck />}
                Approve starter
              </Button>
            </section>
          )}
          <Panel title={`Starter pack (${s.progress.done} of ${s.progress.total} done)`}>
            <ProgressBar p={s.progress} className="mb-4" />
            <ol className="grid gap-3">
              {s.tasks.map((t) => (
                <TaskItem key={t.id} starter={s} task={t} canReview={open} onSaved={refresh} />
              ))}
            </ol>
          </Panel>
        </div>
        <div className="grid min-w-0 content-start gap-4">
          {open && <LinkPanel s={s} onSaved={refresh} />}
          <Panel title="Details">
            <dl className="grid gap-3 text-[15px]">
              <Row label="Email">{s.email || 'Not given'}</Row>
              <Row label="Mobile">{s.mobile || 'Not given'}</Row>
              <Row label="Invited by">
                {s.invitedByName} · {ukDate(s.createdAt)}
              </Row>
              {s.approvedAt && (
                <Row label="Approved">
                  {s.approvedBy} · {ukDate(s.approvedAt, true)}
                </Row>
              )}
            </dl>
            {open && (
              <Button
                variant="outline"
                className="mt-4 h-10 w-full text-destructive hover:text-destructive"
                disabled={withdraw.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      'Withdraw this starter? Their link stops working. Their answers are kept.',
                    )
                  )
                    withdraw.mutate()
                }}
              >
                <UserX />
                Withdraw starter
              </Button>
            )}
          </Panel>
          <Timeline events={s.events} />
        </div>
      </div>
      <EditStarterDialog
        starter={s}
        open={editing}
        onOpenChange={setEditing}
        onSaved={(x) => {
          refresh(x)
          setEditing(false)
        }}
      />
    </>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="break-words">{children}</dd>
    </div>
  )
}

function LinkPanel({ s, onSaved }: { s: StarterDetail; onSaved: (s: StarterDetail) => void }) {
  const link = useMutation({
    mutationFn: (v: { renew: boolean; sendEmail: boolean }) =>
      api.post<StarterDetail>(`/starters/${s.id}/link`, v),
    onSuccess: (x, v) => {
      onSaved(x)
      toast.success(
        x.emailed === 'queued'
          ? 'Link emailed to the starter.'
          : v.renew
            ? 'New link made. The old one no longer works.'
            : 'Link extended for another 30 days.',
      )
    },
    onError: (e) => reportError(e),
  })
  return (
    <Panel title="Their private link" icon={<Send />}>
      {s.link ? (
        <div className="grid gap-3">
          <p className="text-sm text-muted-foreground">
            {s.linkExpired
              ? 'This link has expired. Extend it or make a new one.'
              : `Works until ${ukDate(s.expiresAt)}. Anyone with it can see their pack, so only send it to them.`}
          </p>
          <code
            className="block rounded-lg border bg-muted px-3 py-2 text-[13px] break-all"
            data-testid="starter-link"
          >
            {s.link}
          </code>
          <div className="grid gap-2">
            <Button
              className="h-10"
              onClick={() =>
                void navigator.clipboard
                  ?.writeText(s.link!)
                  .then(() => toast.success('Link copied'))
              }
            >
              <Copy />
              Copy link
            </Button>
            <Button
              variant="outline"
              className="h-10"
              disabled={link.isPending || !s.email}
              onClick={() => link.mutate({ renew: false, sendEmail: true })}
            >
              <Mail />
              {s.linkExpired ? 'Extend and email again' : 'Email the link again'}
            </Button>
            <Button
              variant="outline"
              className="h-10"
              disabled={link.isPending}
              onClick={() => {
                if (window.confirm('Make a new link? The current link will stop working.'))
                  link.mutate({ renew: true, sendEmail: false })
              }}
            >
              <RotateCcw />
              Replace the link
            </Button>
          </div>
        </div>
      ) : (
        <Button
          className="h-10 w-full"
          disabled={link.isPending}
          onClick={() => link.mutate({ renew: true, sendEmail: false })}
        >
          <RotateCcw />
          Make a link
        </Button>
      )}
    </Panel>
  )
}

function TaskItem({
  starter,
  task: t,
  canReview,
  onSaved,
}: {
  starter: StarterDetail
  task: StarterTask
  canReview: boolean
  onSaved: (s: StarterDetail) => void
}) {
  const [returning, setReturning] = useState(false)
  const [note, setNote] = useState('')
  const Icon = KIND_ICON[t.kind]
  const review = useMutation({
    mutationFn: (v: { decision: 'accept' | 'reject'; note?: string }) =>
      api.post<StarterDetail>(`/starters/${starter.id}/tasks/${t.id}/review`, {
        ...v,
        expectedVersion: t.version,
      }),
    onSuccess: (x, v) => {
      onSaved(x)
      setReturning(false)
      setNote('')
      toast.success(v.decision === 'accept' ? 'Marked as checked' : 'Sent back to the starter')
    },
    onError: (e) => reportError(e),
  })
  const state =
    t.status === 'rejected'
      ? { label: 'Sent back', cls: 'bg-destructive/10 text-[#9a2f0c] dark:text-destructive' }
      : t.status === 'done' && t.reviewedAt
        ? { label: 'Checked', cls: 'bg-success/10 text-[#0f5c3b] dark:text-success' }
        : t.status === 'done'
          ? { label: 'Done', cls: 'bg-primary/10 text-primary' }
          : { label: t.required ? 'To do' : 'Optional', cls: 'bg-secondary text-muted-foreground' }

  return (
    <li className="rounded-lg border p-4" data-testid={`task-${t.kind}`}>
      <div className="flex items-start gap-3">
        <span
          className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground"
          aria-hidden
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-medium">{t.name}</h3>
            <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', state.cls)}>
              {state.label}
            </span>
          </div>
          <p className="text-[13px] text-muted-foreground">
            {KIND_LABEL[t.kind]}
            {t.completedAt && ` · completed ${ukDate(t.completedAt, true)}`}
            {t.reviewedAt && ` · checked by ${t.reviewedBy}, ${ukDate(t.reviewedAt)}`}
          </p>
          {t.status === 'rejected' && t.reviewNote && (
            <p className="mt-2 rounded-md bg-destructive/5 px-3 py-2 text-sm">
              Sent back: {t.reviewNote}
            </p>
          )}
          {t.status === 'done' && <TaskAnswer task={t} />}
        </div>
      </div>
      {canReview && t.status === 'done' && (
        <div className="mt-3 flex flex-wrap gap-2 sm:pl-12">
          {!t.reviewedAt && (
            <Button
              size="sm"
              className="h-9"
              disabled={review.isPending}
              onClick={() => review.mutate({ decision: 'accept' })}
            >
              <BadgeCheck />
              {t.kind === 'upload' ? 'Mark as checked' : 'Accept'}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-9"
            disabled={review.isPending}
            onClick={() => setReturning(true)}
          >
            <RotateCcw />
            Send back
          </Button>
        </div>
      )}
      <Dialog open={returning} onOpenChange={setReturning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send back: {t.name}</DialogTitle>
            <DialogDescription>
              The starter sees this note on their link and can put it right.
            </DialogDescription>
          </DialogHeader>
          <label className="grid gap-1.5 text-sm font-medium">
            What needs changing?
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="text-base md:text-[15px]"
              placeholder="For example: the photo is blurred, please take it again in good light."
            />
          </label>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="h-10" onClick={() => setReturning(false)}>
              Cancel
            </Button>
            <Button
              className="h-10"
              disabled={!note.trim() || review.isPending}
              onClick={() => review.mutate({ decision: 'reject', note: note.trim() })}
            >
              {review.isPending ? <Loader2 className="animate-spin" /> : <RotateCcw />}
              Send back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  )
}

function TaskAnswer({ task: t }: { task: StarterTask }) {
  const d = t.data ?? {}
  if (t.kind === 'form')
    return (
      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {Object.entries(ANSWER_LABELS)
          .filter(([k]) => k in d && String(d[k] ?? '').trim())
          .map(([k, label]) => (
            <div key={k} className="min-w-0">
              <dt className="text-[13px] text-muted-foreground">{label}</dt>
              <dd className="text-[15px] break-words whitespace-pre-line">
                {k === 'drivingLicence' ? (d[k] === 'yes' ? 'Yes' : 'No') : String(d[k])}
              </dd>
            </div>
          ))}
      </dl>
    )
  if (t.kind === 'sign') {
    const sig = d.signature as { type: string; text: string; image: string } | undefined
    const ev = d.evidence as { signedAtUtc: string; ip: string; contentHash: string } | undefined
    return (
      <div className="mt-3 grid gap-2">
        <div className="grid h-20 max-w-xs place-items-center rounded-lg border bg-white px-3">
          {sig?.type === 'drawn' ? (
            <img
              src={sig.image}
              alt={`Signature of ${String(d.signerName)}`}
              className="max-h-16"
            />
          ) : (
            <TypedSignature text={sig?.text ?? ''} />
          )}
        </div>
        {ev && (
          <p className="text-[13px] break-all text-muted-foreground">
            Signed by {String(d.signerName)} on {ukDate(ev.signedAtUtc, true)} · IP {ev.ip} ·
            fingerprint {ev.contentHash.slice(0, 12)}…
          </p>
        )}
      </div>
    )
  }
  if (t.kind === 'acknowledge')
    return (
      <p className="mt-2 text-[13px] text-muted-foreground">Confirmed as read and understood.</p>
    )
  return (
    <ul className="mt-3 grid gap-2 sm:grid-cols-2">
      {t.files.map((f) => (
        <FileItem key={f.id} f={f} />
      ))}
    </ul>
  )
}

function FileItem({ f }: { f: StarterFile }) {
  const image = f.mime.startsWith('image/')
  return (
    <li className="grid min-w-0 gap-2 rounded-lg border p-2">
      {image ? (
        <a href={fileUrl(f.id)} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={fileUrl(f.id)}
            alt={f.originalName}
            loading="lazy"
            className="aspect-[4/3] w-full rounded-md bg-muted object-cover"
          />
        </a>
      ) : (
        <span className="flex aspect-[4/3] items-center justify-center rounded-md bg-muted">
          <FileText className="size-8 text-muted-foreground" aria-hidden />
        </span>
      )}
      <span className="min-w-0 px-1">
        <span className="block truncate text-sm font-medium">{f.originalName}</span>
        <span className="block text-[13px] text-muted-foreground">
          {formatSize(f.size)} · {ukDate(f.createdAt, true)}
        </span>
      </span>
      <span className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-9 flex-1"
          render={<a href={fileUrl(f.id)} target="_blank" rel="noopener noreferrer" />}
        >
          <Eye />
          Open
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9 flex-1"
          render={<a href={fileUrl(f.id, true)} />}
        >
          <Download />
          Save
        </Button>
      </span>
    </li>
  )
}
